import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

interface Props {
  strategyId?: string;
  onComplete: (id?: string) => void;
}

const UploadIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="17 8 12 3 7 8"></polyline>
    <line x1="12" y1="3" x2="12" y2="15"></line>
  </svg>
);

const OnboardingModule: React.FC<Props> = ({ strategyId, onComplete }) => {
  const [projectName, setProjectName] = useState('');
  const [briefFile, setBriefFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dots, setDots] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (loading) {
      interval = setInterval(() => {
        setDots(prev => prev.length >= 3 ? '' : prev + '.');
      }, 400);
    } else {
      setDots('');
    }
    return () => clearInterval(interval);
  }, [loading]);

  const handleSubmit = async () => {
    if (loading) return;
    if (!briefFile || !projectName) return alert('Completa el nombre del proyecto y sube un archivo.');
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('project_name', projectName);
      formData.append('brief_file', briefFile);
      const clientId = searchParams.get('client_id');
      const clientName = searchParams.get('client_name');
      if (clientId) formData.append('client_id', clientId);
      if (clientName) formData.append('client_name', clientName);

      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8050/api/v1'}/strategies`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: formData
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Error al crear estrategia');
      }
      let data;
      try {
        const textResponse = await res.text();
        data = JSON.parse(textResponse);
      } catch (e) {
        throw new Error(`El servidor devolvió una respuesta no válida (posible Timeout o error 502/504). Contacta al administrador.`);
      }
      onComplete(data.id);
    } catch (err: any) {
      alert(err.message || err);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (briefFile && projectName && !loading) {
      handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [briefFile]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setBriefFile(e.dataTransfer.files[0]);
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Onboarding: Set Up</h2>
      <p style={styles.description}>Sube el Brief del Cliente. Esto se inyectará en el contexto global de la estrategia.</p>

      <div style={styles.formGroup}>
        <label style={styles.label}>Nombre del Proyecto</label>
        <input style={styles.input} value={projectName} onChange={e => setProjectName(e.target.value)} placeholder="Ej. Campaña Verano 2026" />
      </div>

      <div style={{...styles.formGroup, marginTop: '16px'}}>
        <label style={styles.darkLabel}>Archivo de Carga</label>
        <div 
          style={{
            ...styles.dropZone,
            ...(isDragging ? styles.dropZoneActive : {})
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div style={styles.iconWrapper}>
            <UploadIcon />
          </div>
          <div style={styles.dropZoneText}>Elegir archivo o arrastrar</div>
          <div style={styles.dropZoneLimit}>Límite: 50 MB</div>
          
          <button type="button" style={styles.exploreBtn} onClick={triggerFileSelect}>
            Explorar Archivos
          </button>
          
          <input 
            type="file" 
            ref={fileInputRef}
            accept=".pdf,.doc,.docx" 
            onChange={e => setBriefFile(e.target.files ? e.target.files[0] : null)} 
            style={{ display: 'none' }} 
          />
          {briefFile && (
            <div style={styles.fileSelected}>
              ✓ {briefFile.name}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div style={styles.loadingStatus}>
          <div style={styles.spinner}></div>
          <span style={{color: 'var(--accent)', fontWeight: 500}}>Procesando{dots}</span>
        </div>
      ) : (
        <button style={styles.button} onClick={handleSubmit} disabled={!briefFile || !projectName}>
          Iniciar Estrategia
        </button>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { display: 'flex', flexDirection: 'column', gap: '16px' },
  title: { fontSize: '24px', color: 'var(--text-primary)', marginBottom: '8px' },
  description: { color: 'var(--text-secondary)', marginBottom: '24px' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
  label: { fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', letterSpacing: '0.02em' },
  darkLabel: { fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' },
  input: { padding: '12px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)', borderRadius: '4px', fontSize: '14px' },
  dropZone: {
    border: '2px dashed var(--border)',
    borderRadius: '8px',
    padding: '48px 24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    transition: 'all 0.2s',
    background: 'var(--bg-hover)'
  },
  dropZoneActive: {
    borderColor: 'var(--accent)',
    background: 'rgba(59, 130, 246, 0.05)'
  },
  iconWrapper: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: 'rgba(59, 130, 246, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--accent)'
  },
  dropZoneText: {
    color: 'var(--text-primary)',
    fontSize: '14px',
    fontWeight: 500
  },
  dropZoneLimit: {
    color: 'var(--text-secondary)',
    fontSize: '12px'
  },
  exploreBtn: {
    background: 'transparent',
    border: '1px solid var(--border)',
    color: 'var(--text-primary)',
    padding: '8px 16px',
    borderRadius: '4px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    marginTop: '8px'
  },
  fileSelected: {
    marginTop: '16px',
    color: 'var(--success)',
    fontSize: '14px',
    fontWeight: 500,
    background: 'rgba(16, 185, 129, 0.1)',
    padding: '8px 16px',
    borderRadius: '4px'
  },
  button: {
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '24px',
    alignSelf: 'flex-start'
  },
  loadingStatus: { 
    display: 'flex', 
    alignItems: 'center', 
    gap: '12px', 
    marginTop: '24px',
    alignSelf: 'flex-start'
  },
  spinner: { 
    width: '16px', 
    height: '16px', 
    border: '2px solid var(--accent)', 
    borderTopColor: 'transparent', 
    borderRadius: '50%', 
    animation: 'spin 1s linear infinite' 
  }
};

export default OnboardingModule;
